import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Monitor, Apple, Download, ArrowLeft, ExternalLink } from 'lucide-react';

type Platform = 'macOS-arm' | 'macOS-intel' | 'windows' | 'unknown';

function detectPlatform(): Platform {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('mac')) {
    // Apple Silicon detection via WebGL renderer or platform
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl');
    const debugExt = gl?.getExtension('WEBGL_debug_renderer_info');
    if (debugExt) {
      const renderer = gl?.getParameter(debugExt.UNMASKED_RENDERER_WEBGL) || '';
      if (typeof renderer === 'string' && renderer.toLowerCase().includes('apple')) {
        return 'macOS-arm';
      }
    }
    return 'macOS-arm'; // Default to ARM for modern Macs
  }
  if (ua.includes('win')) return 'windows';
  return 'unknown';
}

const GITHUB_RELEASES = 'https://github.com/armonia/zenda/releases/latest';

const platforms = {
  'macOS-arm': {
    label: 'macOS (Apple Silicon)',
    icon: Apple,
    filenamePattern: '_aarch64.dmg',
    note: 'Per Mac con chip M1/M2/M3/M4',
  },
  'macOS-intel': {
    label: 'macOS (Intel)',
    icon: Apple,
    filenamePattern: '_x64.dmg',
    note: 'Per Mac con processore Intel',
  },
  windows: {
    label: 'Windows',
    icon: Monitor,
    filenamePattern: '_x64-setup.exe',
    note: 'Windows 10 o successivo',
  },
} as const;

export function DownloadPage() {
  const [detected, setDetected] = useState<Platform>('unknown');

  useEffect(() => {
    setDetected(detectPlatform());
  }, []);

  const primary = detected !== 'unknown' ? detected : 'macOS-arm';
  const others = (Object.keys(platforms) as Platform[]).filter(
    (p) => p !== primary && p !== 'unknown'
  );

  return (
    <div className="min-h-screen bg-surface-primary gradient-radial flex flex-col">
      {/* Header */}
      <div className="p-6">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-text-tertiary hover:text-text-primary transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna al login
        </Link>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6 pb-20">
        <div className="max-w-lg w-full text-center animate-fade-in">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-2xl bg-linear-to-br from-accent-violet to-accent-blue flex items-center justify-center shadow-xl shadow-accent-violet/25">
              <span className="text-3xl font-bold text-white">Z</span>
            </div>
          </div>

          <h1 className="text-3xl font-bold text-text-primary mb-2">
            Scarica Zenda Desktop
          </h1>
          <p className="text-text-tertiary mb-10">
            App nativa per macOS e Windows. Stessa esperienza, piu' veloce.
          </p>

          {/* Primary download */}
          <PlatformCard platform={primary} isPrimary />

          {/* Other platforms */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            {others.map((p) => (
              <PlatformCard key={p} platform={p} />
            ))}
          </div>

          {/* All releases link */}
          <a
            href={GITHUB_RELEASES}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-8 text-xs text-text-quaternary hover:text-text-tertiary transition-colors"
          >
            Tutte le versioni su GitHub
            <ExternalLink className="w-3 h-3" />
          </a>

          {/* Note */}
          <p className="mt-6 text-xs text-text-quaternary max-w-sm mx-auto">
            L'app non e' ancora firmata. Su macOS: click destro &gt; Apri.
            Su Windows: clicca "Maggiori info" &gt; "Esegui comunque".
          </p>
        </div>
      </div>
    </div>
  );
}

function PlatformCard({
  platform,
  isPrimary = false,
}: {
  platform: Platform;
  isPrimary?: boolean;
}) {
  if (platform === 'unknown') return null;

  const info = platforms[platform];
  const Icon = info.icon;

  if (isPrimary) {
    return (
      <a
        href={GITHUB_RELEASES}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex items-center justify-center gap-3 px-8 py-4 rounded-xl bg-linear-to-r from-accent-violet to-accent-blue text-white font-medium shadow-lg shadow-accent-violet/25 hover:shadow-xl hover:shadow-accent-violet/30 transition-all hover:scale-[1.02]"
      >
        <Download className="w-5 h-5" />
        <span>Scarica per {info.label}</span>
      </a>
    );
  }

  return (
    <a
      href={GITHUB_RELEASES}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-border-primary text-text-secondary text-sm hover:bg-surface-secondary hover:border-border-secondary transition-all"
    >
      <Icon className="w-4 h-4" />
      <span>{info.label}</span>
    </a>
  );
}
